import { useEffect, useState, useCallback } from "react";
import { Wheat, LogOut, RefreshCw, Lock, Inbox, CakeSlice, Mail, Phone, Calendar, Users, ShoppingBag, Package } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ProductsAdmin from "../components/admin/ProductsAdmin";
import SettingsAdmin from "../components/admin/SettingsAdmin";
import { useProducts } from "../context/ProductsContext";
import { getCustomOrders, getInquiries, getPaidOrders, adminLogin, adminLogout, adminMe, adminForgotPassword, adminResetPassword, updateOrderStatus } from "../lib/api";

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [updatingId, setUpdatingId] = useState("");
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [paidOrders, setPaidOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { products } = useProducts();

  // A product/settings save found the session expired.
  const onUnauthorized = useCallback(() => {
    setAuthed(false);
    setSessionExpired(true);
    setMode("login");
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, i, p] = await Promise.all([getCustomOrders(), getInquiries(), getPaidOrders()]);
      setOrders(o);
      setInquiries(i);
      setPaidOrders(p);
      setAuthed(true);
    } catch (e) {
      if (e.message === "unauthorized") {
        setAuthed(false);
        setSessionExpired(true);
        setMode("login");
      } else {
        toast.error("Could not reach the server.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get("reset");
    if (rt) {
      setResetToken(rt);
      setMode("reset");
      setChecking(false);
      return;
    }
    // Restore an existing session from the httpOnly cookie (no token in JS).
    (async () => {
      if (await adminMe()) await load();
      setChecking(false);
    })();
  }, [load]);

  const forgot = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await adminForgotPassword(email.trim());
      toast.success("If that email belongs to an admin, a reset link is on its way.");
      setMode("login");
    } catch (err) {
      toast.error("Could not send reset email. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await adminResetPassword(resetToken, newPassword);
      toast.success("Password updated. Please sign in.");
      window.history.replaceState({}, "", window.location.pathname);
      setNewPassword("");
      setResetToken("");
      setMode("login");
    } catch (err) {
      toast.error(err.message || "Reset link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setSessionExpired(false);
    try {
      await adminLogin(email.trim(), password);
      setPassword("");
      await load();
    } catch (err) {
      toast.error(err.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (sessionId, status) => {
    setUpdatingId(sessionId + status);
    try {
      const res = await updateOrderStatus(sessionId, status);
      const sent = res?.email?.sent;
      toast.success(sent ? "Status updated — customer emailed." : "Status updated.");
      await load();
    } catch (err) {
      if (err.message === "unauthorized") {
        setAuthed(false);
        setSessionExpired(true);
      } else {
        toast.error(err.message || "Could not update status.");
      }
    } finally {
      setUpdatingId("");
    }
  };

  const logout = async () => {
    await adminLogout();
    setEmail("");
    setPassword("");
    setAuthed(false);
    setSessionExpired(false);
    setOrders([]);
    setInquiries([]);
  };

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream grain-bg" data-testid="admin-checking">
        <RefreshCw size={28} className="animate-spin text-olive" />
      </div>
    );
  }

  if (!authed) {
    const Brand = () => (
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-11 h-11 rounded-full bg-olive text-cream">
          <Wheat size={22} />
        </span>
        <div>
          <p className="font-serif text-lg text-ink leading-none">Healthy Home Bakery</p>
          <p className="text-xs text-olive font-semibold mt-1 uppercase tracking-wider">Admin Console</p>
        </div>
      </div>
    );
    const cardCls =
      "w-full max-w-sm bg-white rounded-3xl border border-[#EFE9E0] p-8 shadow-[0_30px_70px_-50px_rgba(45,40,37,0.5)]";

    if (mode === "reset") {
      return (
        <div className="min-h-screen grid place-items-center bg-cream grain-bg px-4">
          <form onSubmit={reset} data-testid="admin-reset-form" className={cardCls}>
            <Brand />
            <h2 className="font-serif text-lg text-ink mb-1">Set a new password</h2>
            <p className="text-xs text-[#787069] mb-4">Choose a new password for your admin account.</p>
            <Label htmlFor="admin-new-password" className="flex items-center gap-1.5">
              <Lock size={14} /> New password
            </Label>
            <Input
              id="admin-new-password"
              data-testid="admin-new-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              className="mt-2 h-12 bg-cream"
            />
            <Button type="submit" data-testid="admin-reset-btn" disabled={loading}
              className="mt-5 w-full h-12 rounded-full bg-olive hover:bg-olive-hover text-cream">
              {loading ? "Saving..." : "Update password"}
            </Button>
          </form>
        </div>
      );
    }

    if (mode === "forgot") {
      return (
        <div className="min-h-screen grid place-items-center bg-cream grain-bg px-4">
          <form onSubmit={forgot} data-testid="admin-forgot-form" className={cardCls}>
            <Brand />
            <h2 className="font-serif text-lg text-ink mb-1">Reset your password</h2>
            <p className="text-xs text-[#787069] mb-4">Enter your admin email and we'll send you a reset link.</p>
            <Label htmlFor="admin-forgot-email" className="flex items-center gap-1.5">
              <Mail size={14} /> Email
            </Label>
            <Input
              id="admin-forgot-email"
              data-testid="admin-forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
              className="mt-2 h-12 bg-cream"
            />
            <Button type="submit" data-testid="admin-forgot-btn" disabled={loading}
              className="mt-5 w-full h-12 rounded-full bg-olive hover:bg-olive-hover text-cream">
              {loading ? "Sending..." : "Send reset link"}
            </Button>
            <button type="button" data-testid="admin-back-to-login" onClick={() => setMode("login")}
              className="mt-4 w-full text-xs text-olive hover:underline">
              Back to sign in
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className="min-h-screen grid place-items-center bg-cream grain-bg px-4">
        <form onSubmit={login} data-testid="admin-login-form" className={cardCls}>
          <Brand />
          {sessionExpired && (
            <div data-testid="admin-session-expired" className="mb-4 rounded-xl bg-[#FDF5F0] border border-[#F0D9CB] px-4 py-3 text-sm text-[#8A5A3B]">
              Your session expired. Please sign in again.
            </div>
          )}
          <Label htmlFor="admin-email" className="flex items-center gap-1.5">
            <Mail size={14} /> Email
          </Label>
          <Input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="username"
            className="mt-2 h-12 bg-cream"
          />
          <Label htmlFor="admin-password" className="flex items-center gap-1.5 mt-4">
            <Lock size={14} /> Password
          </Label>
          <Input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
            className="mt-2 h-12 bg-cream"
          />
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-5 w-full h-12 rounded-full bg-olive hover:bg-olive-hover text-cream"
          >
            {loading ? "Checking..." : "Sign in"}
          </Button>
          <button type="button" data-testid="admin-forgot-link" onClick={() => setMode("forgot")}
            className="mt-4 w-full text-xs text-olive hover:underline">
            Forgot password?
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream grain-bg" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#EFE9E0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-olive text-cream">
              <Wheat size={20} />
            </span>
            <div>
              <p className="font-serif text-ink leading-none">Admin Dashboard</p>
              <p className="text-xs text-olive font-semibold mt-0.5 uppercase tracking-wider">Healthy Home Bakery</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              data-testid="admin-refresh-btn"
              onClick={() => load()}
              disabled={loading}
              className="rounded-full border-[#E8E2D9] bg-transparent h-10"
            >
              <RefreshCw size={16} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-10"
            >
              <LogOut size={16} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 max-w-2xl">
          <Stat icon={ShoppingBag} label="Paid Orders" value={paidOrders.length} />
          <Stat icon={CakeSlice} label="Custom Orders" value={orders.length} />
          <Stat icon={Inbox} label="Inquiries" value={inquiries.length} />
          <Stat icon={Package} label="Products" value={products.length} />
        </div>

        <Tabs defaultValue="paid">
          <TabsList className="bg-white border border-[#EFE9E0] h-auto flex-wrap justify-start">
            <TabsTrigger value="paid" data-testid="admin-tab-paid">Paid Orders</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Custom Orders</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-5">
            <ProductsAdmin onUnauthorized={onUnauthorized} />
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            <SettingsAdmin onUnauthorized={onUnauthorized} />
          </TabsContent>

          <TabsContent value="paid" className="mt-5">
            {paidOrders.length === 0 ? (
              <Empty text="No paid basket orders yet." />
            ) : (
              <div className="space-y-4" data-testid="admin-paid-list">
                {paidOrders.map((o) => (
                  <div key={o.id} data-testid={`admin-paid-${o.id}`} className="bg-white rounded-2xl border border-[#EFE9E0] p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-serif text-lg text-ink">{o.name || "Customer"}</h3>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#6E6760]">
                          {o.email && <span className="inline-flex items-center gap-1"><Mail size={14} />{o.email}</span>}
                          <span className="inline-flex items-center gap-1 capitalize"><ShoppingBag size={14} />{o.fulfillment || "pickup"}</span>
                        </div>
                      </div>
                      <span className="font-serif text-xl text-ink">${Number(o.amount || 0).toFixed(2)}</span>
                    </div>
                    <ul className="mt-3 space-y-1 text-sm text-[#524B46]">
                      {(o.items || []).map((it, idx) => (
                        <li key={idx}>• {it}</li>
                      ))}
                    </ul>
                    {o.fulfillment === "delivery" && o.address && (
                      <p className="mt-3 text-sm text-[#524B46] bg-[#F5F0EA] rounded-lg px-3 py-2">🚚 {o.address}</p>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-[#F0EBE3]">
                      {o.order_status && (
                        <span data-testid={`admin-order-status-${o.id}`} className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F3F7F1] text-[#3E5334]">
                          {STATUS_LABELS[o.order_status] || o.order_status}
                        </span>
                      )}
                      <div className="flex flex-wrap gap-2 ml-auto">
                        {o.fulfillment === "delivery" ? (
                          <StatusButton testid={`admin-set-out_for_delivery-${o.id}`}
                            busy={updatingId === o.session_id + "out_for_delivery"}
                            onClick={() => setStatus(o.session_id, "out_for_delivery")}>
                            Out for Delivery
                          </StatusButton>
                        ) : (
                          <StatusButton testid={`admin-set-ready-${o.id}`}
                            busy={updatingId === o.session_id + "ready"}
                            onClick={() => setStatus(o.session_id, "ready")}>
                            Ready for Pickup
                          </StatusButton>
                        )}
                        <StatusButton testid={`admin-set-completed-${o.id}`} variant="olive"
                          busy={updatingId === o.session_id + "completed"}
                          onClick={() => setStatus(o.session_id, "completed")}>
                          Completed
                        </StatusButton>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-[#9a938c]">Paid {fmt(o.updated_at || o.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="mt-5">
            {orders.length === 0 ? (
              <Empty text="No custom orders yet." />
            ) : (
              <div className="space-y-4" data-testid="admin-orders-list">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-2xl border border-[#EFE9E0] p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-serif text-lg text-ink">{o.name}</h3>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#6E6760]">
                          <span className="inline-flex items-center gap-1"><Mail size={14} />{o.email}</span>
                          {o.phone && <span className="inline-flex items-center gap-1"><Phone size={14} />{o.phone}</span>}
                          <span className="inline-flex items-center gap-1"><Calendar size={14} />{o.event_date}</span>
                          <span className="inline-flex items-center gap-1"><Users size={14} />{o.servings} servings</span>
                        </div>
                      </div>
                      <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F3F7F1] text-[#3E5334] capitalize">
                        {o.status || "requested"} · {o.fulfillment}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <Tag>Flavor: {o.base_flavor}</Tag>
                      <Tag>Filling: {o.filling}</Tag>
                    </div>
                    {o.dietary_notes && (
                      <p className="mt-3 text-sm text-[#524B46] bg-[#F5F0EA] rounded-lg px-3 py-2">📝 {o.dietary_notes}</p>
                    )}
                    <p className="mt-3 text-xs text-[#9a938c]">Received {fmt(o.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="inquiries" className="mt-5">
            {inquiries.length === 0 ? (
              <Empty text="No inquiries yet." />
            ) : (
              <div className="space-y-4" data-testid="admin-inquiries-list">
                {inquiries.map((q) => (
                  <div key={q.id} data-testid={`admin-inquiry-${q.id}`} className="bg-white rounded-2xl border border-[#EFE9E0] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-serif text-lg text-ink">{q.name}</h3>
                      <p className="text-xs text-[#9a938c]">{fmt(q.created_at)}</p>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#6E6760]">
                      <span className="inline-flex items-center gap-1"><Mail size={14} />{q.email}</span>
                      {q.phone && <span className="inline-flex items-center gap-1"><Phone size={14} />{q.phone}</span>}
                    </div>
                    <p className="mt-3 text-sm text-[#524B46] leading-relaxed">{q.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const STATUS_LABELS = {
  ready: "Ready for pickup",
  out_for_delivery: "Out for delivery",
  completed: "Completed",
};

const StatusButton = ({ children, onClick, busy, testid, variant }) => (
  <Button
    data-testid={testid}
    onClick={onClick}
    disabled={busy}
    className={
      variant === "olive"
        ? "h-9 rounded-full bg-olive hover:bg-olive-hover text-cream text-xs px-4"
        : "h-9 rounded-full bg-terracotta hover:bg-terracotta-hover text-white text-xs px-4"
    }
  >
    {busy ? "Sending..." : children}
  </Button>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#EFE9E0] p-5 flex items-center gap-4">
    <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#F3F7F1] text-olive">
      <Icon size={22} />
    </span>
    <div>
      <p className="font-serif text-2xl text-ink leading-none">{value}</p>
      <p className="text-xs text-[#787069] mt-1">{label}</p>
    </div>
  </div>
);

const Tag = ({ children }) => (
  <span className="px-2.5 py-1 rounded-md bg-[#F5F0EA] text-[#524B46]">{children}</span>
);

const Empty = ({ text }) => (
  <div data-testid="admin-empty" className="text-center py-16 bg-white rounded-2xl border border-[#EFE9E0]">
    <p className="text-[#787069]">{text}</p>
  </div>
);

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
};
